"use client"

import { useEffect } from "react"

// Global error page uses inline styles since it replaces the entire <html> document
// and cannot rely on CSS files or component libraries being loaded.

export default function GlobalError({
    error,
    reset,
}: {
    error: Error & { digest?: string }
    reset: () => void
}) {
    useEffect(() => {
        console.error(error)
    }, [error])

    return (
        <html lang="en">
            <head>
                <title>Error — TokenKickstarter</title>
                <style>{`
                    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

                    * { margin: 0; padding: 0; box-sizing: border-box; }

                    body {
                        font-family: 'Inter', system-ui, -apple-system, sans-serif;
                        background: linear-gradient(135deg, #0a0a0b 0%, #1a0a0a 50%, #0a0a0b 100%);
                        color: #e4e4e7;
                        min-height: 100vh;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        padding: 24px;
                        overflow: hidden;
                    }

                    .error-container {
                        position: relative;
                        z-index: 10;
                        width: 100%;
                        max-width: 480px;
                        animation: fadeInUp 0.8s ease-out forwards;
                    }

                    .error-card {
                        background: rgba(24, 24, 27, 0.8);
                        backdrop-filter: blur(16px);
                        border: 1px solid rgba(239, 68, 68, 0.15);
                        border-radius: 20px;
                        padding: 48px 40px;
                        text-align: center;
                        box-shadow:
                            0 0 60px rgba(239, 68, 68, 0.05),
                            0 25px 50px -12px rgba(0, 0, 0, 0.5);
                    }

                    .icon-wrapper {
                        width: 88px;
                        height: 88px;
                        border-radius: 50%;
                        background: linear-gradient(135deg, rgba(239, 68, 68, 0.15), rgba(245, 158, 11, 0.15));
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        margin: 0 auto 28px;
                        animation: float 3s ease-in-out infinite, pulseGlow 2s ease-in-out infinite;
                    }

                    .icon-wrapper svg {
                        width: 44px;
                        height: 44px;
                        color: #ef4444;
                        animation: iconSpin 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
                    }

                    h2 {
                        font-size: 24px;
                        font-weight: 700;
                        margin-bottom: 12px;
                        color: #fafafa;
                    }

                    .description {
                        color: #a1a1aa;
                        font-size: 15px;
                        line-height: 1.6;
                        margin-bottom: 8px;
                    }

                    .error-digest {
                        font-family: 'SF Mono', 'Fira Code', monospace;
                        font-size: 11px;
                        color: rgba(161, 161, 170, 0.5);
                        margin-bottom: 28px;
                    }

                    .button-group {
                        display: flex;
                        gap: 12px;
                        margin-bottom: 24px;
                    }

                    @media (max-width: 480px) {
                        .button-group { flex-direction: column; }
                    }

                    .btn {
                        flex: 1;
                        padding: 14px 20px;
                        font-size: 14px;
                        font-weight: 600;
                        border-radius: 12px;
                        border: none;
                        cursor: pointer;
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        gap: 8px;
                        transition: all 0.2s ease;
                        text-decoration: none;
                    }

                    .btn:active { transform: scale(0.97); }

                    .btn-primary {
                        background: linear-gradient(135deg, #ef4444, #f59e0b);
                        color: #fff;
                        box-shadow: 0 4px 20px rgba(239, 68, 68, 0.25);
                    }

                    .btn-primary:hover {
                        box-shadow: 0 6px 30px rgba(239, 68, 68, 0.35);
                        transform: translateY(-1px);
                    }

                    .btn-outline {
                        background: transparent;
                        color: #e4e4e7;
                        border: 1px solid rgba(239, 68, 68, 0.2);
                    }

                    .btn-outline:hover {
                        background: rgba(239, 68, 68, 0.08);
                        border-color: rgba(239, 68, 68, 0.3);
                        transform: translateY(-1px);
                    }

                    .home-link {
                        font-size: 13px;
                        color: #71717a;
                    }

                    .home-link a {
                        color: #22c55e;
                        text-decoration: underline;
                        text-underline-offset: 3px;
                        transition: color 0.2s;
                    }

                    .home-link a:hover { color: #4ade80; }

                    .footer-text {
                        margin-top: 28px;
                        padding-top: 20px;
                        border-top: 1px solid rgba(255, 255, 255, 0.06);
                        font-size: 11px;
                        color: rgba(161, 161, 170, 0.4);
                    }

                    /* Background blobs */
                    .blob {
                        position: fixed;
                        border-radius: 50%;
                        filter: blur(80px);
                        pointer-events: none;
                    }

                    .blob-1 {
                        top: 10%;
                        left: 5%;
                        width: 300px;
                        height: 300px;
                        background: rgba(239, 68, 68, 0.08);
                        animation: blobFloat1 8s ease-in-out infinite;
                    }

                    .blob-2 {
                        bottom: 10%;
                        right: 5%;
                        width: 400px;
                        height: 400px;
                        background: rgba(245, 158, 11, 0.06);
                        animation: blobFloat2 10s ease-in-out infinite;
                    }

                    /* Floating spark icons */
                    .spark {
                        position: fixed;
                        color: rgba(245, 158, 11, 0.2);
                        pointer-events: none;
                        animation: sparkle 3s ease-in-out infinite;
                    }

                    .spark:nth-child(3) { top: 15%; left: 15%; animation-delay: 0s; }
                    .spark:nth-child(4) { top: 25%; right: 20%; animation-delay: 0.6s; }
                    .spark:nth-child(5) { bottom: 30%; left: 25%; animation-delay: 1.2s; }
                    .spark:nth-child(6) { bottom: 15%; right: 30%; animation-delay: 1.8s; }

                    @keyframes fadeInUp {
                        from { opacity: 0; transform: translateY(30px); }
                        to { opacity: 1; transform: translateY(0); }
                    }

                    @keyframes float {
                        0%, 100% { transform: translateY(0); }
                        50% { transform: translateY(-12px); }
                    }

                    @keyframes pulseGlow {
                        0%, 100% { box-shadow: 0 0 20px rgba(239, 68, 68, 0.15); }
                        50% { box-shadow: 0 0 40px rgba(239, 68, 68, 0.25); }
                    }

                    @keyframes iconSpin {
                        from { opacity: 0; transform: scale(0) rotate(-180deg); }
                        to { opacity: 1; transform: scale(1) rotate(0); }
                    }

                    @keyframes blobFloat1 {
                        0%, 100% { transform: translate(0, 0) scale(1); }
                        50% { transform: translate(30px, -20px) scale(1.2); }
                    }

                    @keyframes blobFloat2 {
                        0%, 100% { transform: translate(0, 0) scale(1); }
                        50% { transform: translate(-40px, 20px) scale(1.3); }
                    }

                    @keyframes sparkle {
                        0%, 100% { opacity: 0; transform: scale(0.5) rotate(0deg); }
                        50% { opacity: 1; transform: scale(1.2) rotate(180deg); }
                    }
                `}</style>
            </head>
            <body>
                {/* Background blobs */}
                <div className="blob blob-1" />
                <div className="blob blob-2" />

                {/* Spark particles */}
                {[0, 1, 2, 3].map((i) => (
                    <svg key={i} className="spark" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                    </svg>
                ))}

                <div className="error-container">
                    <div className="error-card">
                        {/* Icon */}
                        <div className="icon-wrapper">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                                <path d="M12 9v4" />
                                <path d="M12 17h.01" />
                            </svg>
                        </div>

                        <h2>Something Went Wrong</h2>

                        <p className="description">
                            An unexpected error occurred while processing your request.
                            Don&apos;t worry — your funds and tokens are safe on the blockchain.
                        </p>

                        {error.digest && (
                            <p className="error-digest">Error ID: {error.digest}</p>
                        )}

                        {!error.digest && <div style={{ marginBottom: 20 }} />}

                        <div className="button-group">
                            <button className="btn btn-primary" onClick={reset}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
                                    <path d="M21 3v5h-5" />
                                </svg>
                                Try Again
                            </button>

                            <button className="btn btn-outline" onClick={() => window.history.back()}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="m12 19-7-7 7-7" />
                                    <path d="M19 12H5" />
                                </svg>
                                Go Back
                            </button>
                        </div>

                        <p className="home-link">
                            Or return to the <a href="/">homepage</a>
                        </p>

                        <p className="footer-text">
                            TokenKickstarter — Your funds are always safe on the blockchain
                        </p>
                    </div>
                </div>
            </body>
        </html>
    )
}
